import { useEffect, useRef } from "react";
import { SkillsPopup } from "~/components/assistant/SkillsPopup";
import type { PartialSkill } from "~/components/assistant/types";
import { cn } from "~/lib/utils";

interface SkillAutocompleteProps {
  systemSkills: PartialSkill[];
  userSkills: PartialSkill[];
  isOpen: boolean;
  position: { top: number; left: number } | null;
  searchText: string;
  onSelect: (skillName: string) => void;
  onClose: () => void;
  onAddSkill?: () => void;
  onEditSkill?: (skillId: string) => void;
  onDeleteSkill?: (skillId: string) => void;
  onLearnMore?: () => void;
}

export function SkillAutocomplete({
  systemSkills,
  userSkills,
  isOpen,
  position,
  searchText,
  onSelect,
  onClose,
  onAddSkill,
  onEditSkill,
  onDeleteSkill,
  onLearnMore,
}: SkillAutocompleteProps) {
  const popupRef = useRef<HTMLDivElement>(null);

  // Handle click outside to close
  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;

      // Don't close if clicking inside the popup
      if (popupRef.current && popupRef.current.contains(target)) {
        return;
      }

      // Don't close if clicking inside a Radix Portal (e.g., nested popovers for overflow menus)
      const portalElement = (target as Element).closest("[data-radix-popper-content-wrapper]");
      if (portalElement) {
        return;
      }

      onClose();
    };

    // Add small delay to prevent immediate closing when popup opens
    const timeoutId = setTimeout(() => {
      document.addEventListener("mousedown", handleClickOutside);
    }, 100);

    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen, onClose]);

  if (!isOpen || !position) {
    return null;
  }

  // Calculate position (above cursor)
  const popupStyle: React.CSSProperties = {
    position: "fixed",
    top: position.top - 8,
    left: position.left,
    transform: "translateY(-100%)",
    zIndex: 50,
  };

  return (
    <div
      ref={popupRef}
      className={cn(
        "bg-white rounded-lg shadow-[0_0_0_1px_rgba(84,72,49,0.08),0_3px_8px_rgba(0,0,0,0.12)] overflow-hidden w-84"
      )}
      style={popupStyle}
    >
      <SkillsPopup
        systemSkills={systemSkills}
        userSkills={userSkills}
        searchFilter={searchText}
        onSelectSkill={onSelect}
        onAddSkill={onAddSkill}
        onEditSkill={onEditSkill}
        onDeleteSkill={onDeleteSkill}
        onLearnMore={onLearnMore}
        enableKeyboardNav={true}
        onClose={onClose}
      />
    </div>
  );
}
